import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import logo from '../../assets/logo.webp';

// DEBUG flag - Set to true to enable console logs, false to disable
const DEBUG = false;

// Add a custom logger function
const logger = {
  error: (...args) => {
    if (import.meta.env.DEV) {
      console.error(...args);
    }
  },
  log: (...args) => {
    if (DEBUG) {
      console.log(...args);
    }
  }
};

const DataContext = createContext();
export const DataProvider = ({ children }) => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedLang, setLastFetchedLang] = useState(null);
  const fetchingRef = useRef(false);

  // Log initialization
  if (DEBUG) {
    console.log('[DataContext] Component initialized', {
      language,
      hasData: !!data,
      isLoading,
      hasError: !!error,
    });
  }

  const fetchData = useCallback(async (lang) => {
    if (DEBUG) console.log('[DataContext] fetchData called', {
      lang,
      isFetching: fetchingRef.current,
      lastFetchedLang,
      hasData: !!data
    });

    // Prevent duplicate requests
    if (fetchingRef.current || (lastFetchedLang === lang && data)) {
      if (DEBUG) console.log('[DataContext] Skipping fetch - duplicate request prevented');
      return;
    }

    try {
      if (DEBUG) console.log('[DataContext] Starting fetch process');
      fetchingRef.current = true;
      setError(null);
      setIsLoading(true);

      const url = `/api/homepage?lang=${lang}`;
      if (DEBUG) console.log('[DataContext] Making API request', { url });
      const response = await fetch(url);
      if (DEBUG) console.log('[DataContext] API response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      // Check if response is ok
      if (!response.ok) {
        if (DEBUG) console.log('[DataContext] Response not OK, reading error text');
        const errorText = await response.text();
        if (DEBUG) console.log('[DataContext] Error text received', { errorText });
        let errorMessage = `Failed to fetch data (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          if (DEBUG) console.log('[DataContext] Parsed error JSON', { errorJson });
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch {
          // If not JSON, use the text or default message
          if (DEBUG) console.log('[DataContext] Error text is not JSON, using raw text');
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Parse JSON response
      if (DEBUG) console.log('[DataContext] Parsing JSON response');
      let result;
      try {
        result = await response.json();
        if (DEBUG) console.log('[DataContext] JSON parsed successfully', {
          resultType: typeof result,
          resultLength: result?.length,
          resultPreview: typeof result === 'string' ? result.substring(0, 100) + '...' : result
        });
      } catch (jsonError) {
        console.error('[DataContext] JSON parsing error:', jsonError);
        logger.error('JSON parsing error:', jsonError);
        throw new Error('Invalid response format from server');
      }

      // Validate result
      if (DEBUG) console.log('[DataContext] Validating result', {
        hasResult: !!result,
        resultType: typeof result,
        isString: typeof result === 'string'
      });
      
      if (!result || typeof result !== 'object') {
        console.error('[DataContext] Invalid result format', { result });
        throw new Error('Invalid data format received from server');
      }

      setData(result);
      setLastFetchedLang(lang); // Update last fetched language
      if (DEBUG) console.log('[DataContext] Data set successfully', { lang });
    } catch (error) {
      console.error('[DataContext] Error in fetchData:', error);
      logger.error('Error fetching data:', error);
      setError(error.message || 'An unexpected error occurred');
    } finally {
      if (DEBUG) console.log('[DataContext] Fetch process completed, cleaning up');
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [lastFetchedLang, data]);

  // Fetch data whenever language changes
  useEffect(() => {
    if (DEBUG) console.log('[DataContext] useEffect triggered', {
      language,
      hasData: !!data,
      lastFetchedLang,
      shouldFetch: language && (!data || lastFetchedLang !== language)
    });

    if (language && (!data || lastFetchedLang !== language)) {
      if (DEBUG) console.log('[DataContext] Scheduling fetchData with setTimeout');
      // Use setTimeout to ensure we're not in a render cycle
      const timeoutId = setTimeout(() => {
        if (DEBUG) console.log('[DataContext] setTimeout callback executed, calling fetchData');
        fetchData(language);
      }, 0);
      
      return () => {
        if (DEBUG) console.log('[DataContext] Cleaning up setTimeout');
        clearTimeout(timeoutId);
      };
    } else {
      if (DEBUG) console.log('[DataContext] Skipping fetch - conditions not met');
    }
  }, [language, fetchData, lastFetchedLang, data]);

  if (DEBUG) console.log('[DataContext] Render check', {
    isLoading,
    hasError: !!error,
    hasData: !!data,
    language
  });

  if (isLoading) {
    if (DEBUG) console.log('[DataContext] Rendering loading screen');
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  if (error) {
    if (DEBUG) console.log('[DataContext] Rendering error screen', { error });
    return (
      <div className="flex fixed inset-0 justify-center items-center bg-gray-100">
        <div className="p-8 mx-4 w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="text-center">
            <div className="mb-6">
              <img src={logo} alt="Logo" className="mx-auto mb-4 h-16" />
              <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-800">Oops! Something went wrong</h2>
              <p className="mb-6 text-gray-600">{error || 'We encountered a technical error. Please try again.'}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors duration-200 hover:bg-blue-700"
            >
              <svg className="mr-2 w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (DEBUG) console.log('[DataContext] Rendering DataProvider with context', {
    hasData: !!data,
    isLoading,
    hasError: !!error
  });

  return (
    <DataContext.Provider value={{
      data,
      isLoading,
      error,
      refetch: fetchData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};